// The panel that opens from a program card on the learner's dashboard, in one
// design for an Assessment and a Training: the name on a ruled header, who it
// is from and what they say, what it holds as ruled lines, and the action in a
// footer. The primary action is the brandy button with white type.

import React from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ProgramKindIcon, { type ProgramKind } from '../../../../../components/ui/ProgramKindIcon';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import { formatDate, fromEpochSeconds } from '../../../../../utils/formatDate';
import type { MemberProgramCardData } from '../../home/member-learning-lifecycle/types';
import { ActionButton, BODY, BRANDY, FONT, LABEL, LINE, SECTION_TITLE, TEXT, TEXT_2, TEXT_3 } from '../designSystem';
import ProgramLeaderboard from './ProgramLeaderboard';
import { MOBILE_SX } from '../../../../../mobile/mobileApp';

/** The primary action on a learner's panel: the house primary button, DosenButton (Start, Open). */
export const BrandCta: React.FC<{ children: React.ReactNode; onClick: () => void; disabled?: boolean }> = ({
  children,
  onClick,
  disabled = false
}) => (
  <DosenButton tone="cta" sizeVariant="sm" disabled={disabled} onClick={onClick} sx={{ whiteSpace: 'nowrap' }}>
    {children}
  </DosenButton>
);

export interface PanelLine {
  label: string;
  value: string;
  /** The value's colour, where it carries meaning (a level, done). */
  color?: string;
}

export const ProgramPanel: React.FC<{
  open: boolean;
  title: string;
  kind: ProgramKind;
  /** Who it is from ("From Sarah Byrne"), over their message. */
  from?: string;
  message?: string;
  linesTitle: string;
  lines: PanelLine[];
  /** A line of guidance under the lines. */
  note?: string;
  /** A section under the lines (a Training's program leaderboard). */
  extra?: React.ReactNode;
  primary?: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
  onClose: () => void;
}> = ({ open, title, kind, from, message, linesTitle, lines, note, extra, primary, secondary, onClose }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: 460,
        maxWidth: '96vw',
        bgcolor: '#1C1C1C',
        borderLeft: `1px solid ${LINE}`,
        backgroundImage: 'none',
        // Phone: a full screen of its own, clear of the notch and the home indicator.
        [MOBILE_SX]: {
          width: '100%',
          maxWidth: '100%',
          borderLeft: 'none',
          pt: 'env(safe-area-inset-top)',
          pb: 'env(safe-area-inset-bottom)'
        }
      }
    }}
  >
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: 60,
          px: '20px',
          borderBottom: `1px solid ${LINE}`,
          [MOBILE_SX]: { minHeight: 56, py: '8px', pl: '16px', pr: '8px' }
        }}
      >
        <Typography
          sx={{
            ...SECTION_TITLE,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // Phone: the whole name, over two lines when it needs them.
            [MOBILE_SX]: { overflow: 'visible', whiteSpace: 'normal', lineHeight: 1.3, fontSize: 16 }
          }}
        >
          {title}
        </Typography>
        <ProgramKindIcon kind={kind} size={17} />
        <IconButton aria-label="Close" onClick={onClose} sx={{ color: TEXT_2, '&:hover': { color: TEXT } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          [MOBILE_SX]: { p: '16px' }
        }}
      >
        {message && (
          <Box>
            {from && <Typography sx={{ ...LABEL, color: BRANDY, mb: '8px' }}>{from}</Typography>}
            <Typography sx={{ ...BODY, fontSize: 14, color: TEXT, whiteSpace: 'pre-wrap' }}>{message}</Typography>
          </Box>
        )}

        <Box>
          <Typography sx={{ ...LABEL, mb: '8px' }}>{linesTitle}</Typography>
          <Box sx={{ borderTop: `1px solid ${LINE}` }}>
            {lines.map((line) => (
              <Box
                key={line.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: 38,
                  borderBottom: `1px solid ${LINE}`,
                  [MOBILE_SX]: { minHeight: 44, py: '6px' }
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: FONT,
                    fontSize: 13,
                    color: TEXT,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    // Phone: the label keeps its width (up to two thirds) and wraps; the value takes the rest.
                    [MOBILE_SX]: {
                      flex: '0 1 auto',
                      maxWidth: '65%',
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      lineHeight: 1.35
                    }
                  }}
                >
                  {line.label}
                </Typography>
                <Typography
                  sx={{
                    ...LABEL,
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: 12,
                    flexShrink: 0,
                    color: line.color ?? TEXT_3,
                    // Phone: a long value (the next lesson's name) wraps on the right.
                    [MOBILE_SX]: { flex: '1 1 0', minWidth: 0, textAlign: 'right', lineHeight: 1.35 }
                  }}
                >
                  {line.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {note && <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>{note}</Typography>}
        {extra}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          p: '14px 20px',
          borderTop: `1px solid ${LINE}`,
          [MOBILE_SX]: { p: '12px 16px' }
        }}
      >
        <ActionButton onClick={onClose}>Close</ActionButton>
        <Box sx={{ flex: 1 }} />
        {/* Navigation only, so a link rather than a button. */}
        {secondary && (
          <Box
            component="button"
            type="button"
            onClick={secondary.onClick}
            sx={{
              p: 0,
              mr: '8px',
              border: 'none',
              bgcolor: 'transparent',
              color: BRANDY,
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {secondary.label}
          </Box>
        )}
        {primary && <BrandCta onClick={primary.onClick}>{primary.label}</BrandCta>}
      </Box>
    </Box>
  </Drawer>
);

const STATUS_TEXT: Record<MemberProgramCardData['status'], string> = {
  'not-started': 'Not started',
  'in-progress': 'In progress',
  completed: 'Completed'
};
// "40 min", "1 hr 20 min": the same words as the header bar's "40 min remaining".
const hoursMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};
const dateText = (unixSeconds: number) => formatDate(fromEpochSeconds(unixSeconds));

/**
 * What opens from a Training's card: where the learner is in it, and Start or
 * Continue into their next lesson. Program Details opens the full program
 * view (its modules and lessons).
 */
export const TrainingPanel: React.FC<{
  program: MemberProgramCardData | null;
  onClose: () => void;
  /** Into the next lesson. */
  onContinue: (program: MemberProgramCardData) => void;
  /** The full program view. */
  onDetails: (program: MemberProgramCardData) => void;
}> = ({ program, onClose, onContinue, onDetails }) => {
  if (!program) return null;
  const locked = program.locked === true;
  const lines: PanelLine[] = [
    {
      label: 'Status',
      value: STATUS_TEXT[program.status],
      color: program.status === 'completed' ? '#00972D' : program.status === 'in-progress' ? BRANDY : undefined
    },
    { label: 'Progress', value: `${Math.round(program.goalProgress)}%` },
    ...(program.resumeLesson ? [{ label: 'Next lesson', value: program.resumeLesson.lessonName }] : []),
    ...(program.timeRemainingMinutes !== null
      ? [{ label: 'Time remaining', value: hoursMinutes(program.timeRemainingMinutes) }]
      : []),
    ...(program.mcqScore !== null ? [{ label: 'Quiz score', value: `${Math.round(program.mcqScore)}%` }] : []),
    ...(program.dueBy ? [{ label: 'Due by', value: dateText(program.dueBy) }] : []),
    ...(program.owner?.name ? [{ label: 'Owner', value: program.owner.name }] : [])
  ];
  return (
    <ProgramPanel
      open
      kind="training"
      title={program.name}
      linesTitle="Your Progress"
      lines={lines}
      note={locked ? 'Your admin will unlock this Training when the Training is ready for you.' : undefined}
      extra={<ProgramLeaderboard program={program} />}
      secondary={{ label: 'Program Details', onClick: () => onDetails(program) }}
      primary={
        locked || !program.resumeLesson
          ? undefined
          : { label: program.status === 'not-started' ? 'Start' : 'Open', onClick: () => onContinue(program) }
      }
      onClose={onClose}
    />
  );
};
